import { toast } from "react-toastify";
import { handleError } from "../../../utils/helpers";
import { BaseApiResponse, QueryParams } from "../../../utils/types";
import { baseApi } from "../../api";
import { authSliceActions } from "../auth/auth.slice";
import {
  AssignEventDiscountPayload,
  BuyTicketsPayload,
  BuyTicketsResponse,
  CreateDiscountPayload,
  CreateDiscountResponse,
  CreateEvent,
  CreateEventResponse,
  CreateTicket,
  DiscountLogsResponse,
  EditEventResponse,
  EditEventTicketResponse,
  EventStatus,
  GetDiscountResponse,
  GetDiscountsResponse,
  GetEventGuestResponse,
  GetEventGuestsResponse,
  GetEventResponse,
  GetEventsQuery,
  GetEventsResponse,
  GetEventTicketsBreakdownResponse,
  GetEventTicketsResponse,
} from "./event.types";

export const eventApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    createEvent: builder.mutation<CreateEventResponse, CreateEvent>({
      query: (payload) => {
        return {
          url: "events",
          method: "POST",
          body: { ...payload },
        };
      },
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;

          dispatch(authSliceActions.markCreateEvent());
        } catch (err) {
          handleError(err, true);
        }
      },
    }),
    editEvent: builder.mutation<
      CreateEventResponse,
      { id: string; payload: Partial<CreateEvent> }
    >({
      query: ({ id, payload }) => {
        return {
          url: `events/${id}`,
          method: "PUT",
          body: { ...payload },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),
    updateEventStatus: builder.mutation<
      EditEventResponse,
      { id: string; payload: { status: EventStatus } }
    >({
      query: ({ id, payload }) => {
        return {
          url: `events/${id}/status`,
          method: "PATCH",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),
    getEvents: builder.query<GetEventsResponse, Partial<GetEventsQuery>>({
      query: ({ ...params }) => {
        return {
          url: "events",
          method: "GET",
          params: { ...params },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getEvent: builder.query<GetEventResponse, { id?: string; slug?: string }>({
      query: (params) => {
        return {
          url: `events/${params.id ?? params.slug}${params.slug ? "/details" : ""}`,
          method: "GET",
          params: undefined,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getEventTickets: builder.query<
      GetEventTicketsResponse,
      QueryParams & { id?: string; slug?: string }
    >({
      query: ({ id, slug, ...params }) => {
        return {
          url: `events/${slug ? "slug/" : ""}${id ?? slug}/tickets`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getEventTicketsBreakdown: builder.query<
      GetEventTicketsBreakdownResponse,
      QueryParams & { id: string }
    >({
      query: ({ id, ...params }) => {
        return {
          url: `events/${id}/ticket-breakdown`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    addEventTicket: builder.mutation<
      BaseApiResponse,
      { id: string; payload: CreateTicket }
    >({
      query: ({ payload, id }) => {
        return {
          url: `events/${id}/tickets`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          toast.success(resp.data.message);
        } catch (err) {
          handleError(err, true);
        }
      },
    }),
    editEventTicket: builder.mutation<
      EditEventTicketResponse,
      { id: string; payload: Partial<CreateTicket> }
    >({
      query: ({ id, payload }) => {
        return {
          url: `events/ticket/${id}`,
          method: "PUT",
          body: { ...payload },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data.message);
        } catch (err) {
          handleError(err, true);
        }
      },
    }),
    deleteEventTicket: builder.mutation<BaseApiResponse, { id: string }>({
      query: ({ id }) => {
        return {
          url: `events/ticket/${id}`,
          method: "DELETE",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data.message);
        } catch (err) {
          handleError(err, true);
        }
      },
    }),
    getEventGuests: builder.query<
      GetEventGuestsResponse,
      QueryParams & { id: string; search: string; status?: string }
    >({
      query: ({ id, ...params }) => {
        return {
          url: `guests/${id}`,
          method: "GET",
          params: {
            ...params,
            search: params.search === "" ? undefined : params.search,
            status: params.status,
          },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getEventGuest: builder.query<GetEventGuestResponse, { id: string }>({
      query: (params) => {
        return {
          url: `guests/${params.id}/details`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    checkGuestIn: builder.mutation<BaseApiResponse, { id: string }>({
      query: (payload) => {
        return {
          url: `guests/${payload.id}/check-in`,
          method: "PATCH",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data.message);
        } catch (err) {
          handleError(err, true);
        }
      },
    }),
    buyTicktes: builder.mutation<
      BuyTicketsResponse,
      { id: string; data: BuyTicketsPayload }
    >({
      query: (payload) => {
        return {
          url: `guests/${payload.id}/buy-ticket`,
          method: "POST",
          body: payload.data,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),
    createDiscount: builder.mutation<
      CreateDiscountResponse,
      CreateDiscountPayload
    >({
      query: (payload) => {
        return {
          url: `events/discounts`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data.message);
        } catch (err) {
          handleError(err, true);
        }
      },
    }),
    editDiscount: builder.mutation<
      CreateDiscountResponse,
      { id: string; data: Partial<CreateDiscountPayload> }
    >({
      query: (payload) => {
        return {
          url: `events/discounts/${payload.id}`,
          method: "PATCH",
          body: payload.data,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data.message);
        } catch (err) {
          handleError(err, true);
        }
      },
    }),
    deleteDiscount: builder.mutation<BaseApiResponse, { id: string }>({
      query: (payload) => {
        return {
          url: `events/discounts/${payload.id}`,
          method: "DELETE",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data.message);
        } catch (err) {
          handleError(err, true);
        }
      },
    }),
    getDiscounts: builder.query<
      GetDiscountsResponse,
      QueryParams & {
        status?: string;
      }
    >({
      query: (params) => {
        return {
          url: `events/user/all/discounts`,
          method: "GET",
          params: { ...params },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getDiscount: builder.query<
      GetDiscountResponse,
      { code: string; userId: string }
    >({
      query: (params) => {
        return {
          url: `guests/discount/${params.userId}/${params.code}/details`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    validateDiscount: builder.mutation<
      GetDiscountResponse,
      { code: string; userId: string; eventId?: string }
    >({
      query: ({ userId, code, ...params }) => {
        return {
          url: `guests/validate-discount/${userId}/${code}`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    fetchDiscountUsageLogs: builder.query<
      DiscountLogsResponse,
      { code: string; query: QueryParams }
    >({
      query: (params) => {
        return {
          url: `events/${params.code}/discount-logs`,
          method: "GET",
          params: { ...params.query },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    toggleAssignEventDiscount: builder.mutation<
      BaseApiResponse,
      AssignEventDiscountPayload
    >({
      query: (payload) => {
        return {
          url: `events/assign-discounts`,
          method: "PATCH",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data.status === "success" && arg.status === "assign") {
            toast.success("Discount assigned to event");
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),
  }),
});

export const {
  useCreateEventMutation,
  useEditEventMutation,
  useUpdateEventStatusMutation,
  useGetEventsQuery,
  useGetEventQuery,
  useGetEventTicketsQuery,
  useGetEventTicketsBreakdownQuery,
  useAddEventTicketMutation,
  useEditEventTicketMutation,
  useDeleteEventTicketMutation,
  useGetEventGuestsQuery,
  useGetEventGuestQuery,
  useCheckGuestInMutation,
  useBuyTicktesMutation,
  useCreateDiscountMutation,
  useEditDiscountMutation,
  useDeleteDiscountMutation,
  useGetDiscountsQuery,
  useGetDiscountQuery,
  useValidateDiscountMutation,
  useFetchDiscountUsageLogsQuery,
  useToggleAssignEventDiscountMutation,
} = eventApi;
